import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ResetRequest } from './pages/ResetRequest';
import { ResetToken } from './pages/ResetToken';
import { SelectClient } from './pages/SelectClient';
import { Dashboard } from './pages/Dashboard';

/**
 * Root app - providers, router, then page.
 *
 * Routing model is unusual: the SPA is served by Flask from MULTIPLE
 * top-level URLs (/login, /register, /reset-password, /reset-password/:token,
 * eventually /select-client and /dashboard) rather than a single mount-point.
 * So we use `basename="/"` and let React Router match the actual path.
 *
 * PR-D1 scope: adds the /dashboard route - shell only (sidebar,
 * rail, theme toggle, theme-aware logo, /api/platform/me wiring).
 * Chat surface itself is a placeholder until PR-D2.
 *
 * Theme handling:
 *   - Auth shell pages → dark-locked. Driven by the inline
 *     bootstrap in index.html which sets data-theme="dark"
 *     for any URL that isn't /dashboard*.
 *   - Dashboard page  → light/dark via ThemeProvider (mounted
 *     inside <Dashboard/>). Persisted to localStorage as
 *     'platform-ui:dashboard-theme'.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetRequest />} />
          {/* Token-bearing variant. Sits at the same top-level path as
              ResetRequest so URL hygiene is consistent. */}
          <Route path="/reset-password/:token" element={<ResetToken />} />
          <Route path="/select-client" element={<SelectClient />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {/* Fallback. If the SPA was served from a route we don't
              know about, bounce to /login so the user lands somewhere
              sensible rather than an empty white screen. */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
